<script setup>
import { ref, defineProps, defineEmits } from 'vue';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import LoadingAnimation3 from '../loadingIcons/LoadingAnimation3.vue';
import { useModalBackdropClose } from '@/composables/useModalBackdropClose';

const props = defineProps({
  dataID: {
    type: String,
    required: true
  },
  templateId: {
    type: String,
    required: true
  },
  existingPages: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['close', 'pageAdded']);

const loading = ref(false);
const pageName = ref('');
const pageType = ref('custom');
const error = ref('');

const pageTypeOptions = [
  { value: 'custom', label: 'Custom Dashboard', description: 'Drag-and-drop widgets with full customization' },
  { value: 'timeseries', label: 'Time Series', description: 'Line charts showing data trends over time' },
  // { value: 'xetasense', label: 'XetaSense', description: 'XetaSense device configuration and monitoring' },
  // { value: 'pressure-sensor', label: 'Pressure Sensor', description: 'Pressure sensor (TDWLB) configuration and monitoring' }
];

const generatePageId = () => {
  return 'page-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
};

const handleSubmit = async () => {
  error.value = '';
  
  if (!pageName.value.trim()) {
    error.value = 'Page name is required';
    return;
  }

  loading.value = true;

  try {
    const newPage = {
      id: generatePageId(),
      type: pageType.value,
      name: pageName.value.trim(),
      order: props.existingPages.length
    };

    // Add type-specific default structure
    if (pageType.value === 'custom') {
      newPage.layout = [];
      newPage.widgets = {};
    } else if (pageType.value === 'timeseries') {
      newPage.charts = [];
    } else if (pageType.value === 'xetasense' || pageType.value === 'pressure-sensor') {
      // App pages need no extra structure
    }

    // Update Firestore template
    const templateRef = doc(db, 'templates', props.templateId);
    await updateDoc(templateRef, {
      pages: [...props.existingPages, newPage]
    });

    emit('pageAdded', newPage);
    emit('close');
  } catch (err) {
    console.error('Error adding page:', err);
    error.value = 'Failed to add page. Please try again.';
  } finally {
    loading.value = false;
  }
};

const close = () => {
  emit('close');
};

const { onBackdropMousedown, onBackdropMouseup, onContentMousedown } = useModalBackdropClose(close);
</script>

<template>
  <div class="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex justify-center items-center z-50" @mousedown.self="onBackdropMousedown" @mouseup.self="onBackdropMouseup">
    <div class="ui-modal p-6 rounded-lg w-[600px] max-h-[calc(100vh-50px)] overflow-y-auto shadow-2xl" @mousedown="onContentMousedown">
      <!-- Header -->
      <div class="mb-6">
        <div class="h-[3px] w-full bg-mc-red rounded-full mb-5"></div>
        <h2 class="font-extrabold text-xl text-center mb-5 text-mc-ink dark:text-on-dark tracking-tight">Add New Page</h2>
        <p class="text-sm text-ink-muted dark:text-on-dark-muted text-center mt-2">
          Choose a page type and give it a name
        </p>
      </div>

      <form @submit.prevent="handleSubmit" class="space-y-6">
        <!-- Page Name -->
        <div>
          <label for="page-name" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Page Name <span class="text-red-500">*</span></label>
          <input
            id="page-name"
            v-model="pageName"
            type="text"
            class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
            placeholder="e.g., Overview, Sensor Data, Analytics"
            required
            autofocus
          />
        </div>

        <!-- Page Type Selection -->
        <div role="group" aria-labelledby="page-type-label">
          <label id="page-type-label" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Page Type <span class="text-red-500">*</span></label>

          <div class="space-y-3">
            <div
              v-for="option in pageTypeOptions"
              :key="option.value"
              @click="pageType = option.value"
              class="p-4 border-2 rounded-mc-sm cursor-pointer transition-all hover:border-mc-red"
              :class="[
                pageType === option.value
                  ? 'border-mc-red bg-mc-red/5 dark:bg-mc-red/10'
                  : 'border-mc-steel dark:border-line-on-dark'
              ]"
            >
              <div class="flex items-start gap-3">
                <input
                  :id="`page-type-${option.value}`"
                  type="radio"
                  name="page-type"
                  :value="option.value"
                  v-model="pageType"
                  class="mt-1"
                />
                <div>
                  <p class="font-bold text-base text-mc-ink dark:text-on-dark">{{ option.label }}</p>
                  <p class="text-sm text-ink-muted dark:text-on-dark-muted mt-1">{{ option.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p class="text-red-600 dark:text-red-400 text-sm">{{ error }}</p>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-between items-center pt-6 border-t border-mc-steel dark:border-line-on-dark">
          <button
            type="button"
            @click="close"
            class="px-5 py-2 text-xs rounded-full border border-mc-steel dark:border-white/20 text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel/20 dark:hover:bg-white/10 transition-colors duration-160"
            :disabled="loading"
          >
            Cancel
          </button>
          
          <div class="flex items-center gap-3">
            <LoadingAnimation3 v-if="loading" size="30px"></LoadingAnimation3>
            <button 
              v-else 
              type="submit" 
              :disabled="loading" 
              class="default-button"
            >
              Add Page
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
